import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useState } from "react";
import { ParsedCsvSummary } from "./ParsedCsvSummary.js";
import {
  parseTestimonialStatusCsv,
  type ParsedTestimonialStatusCsv,
} from "./parseTestimonialStatusCsv.js";
import { TestimonialStatusRunReport } from "./TestimonialStatusRunReport.js";
import { useTestimonialStatusBatch } from "./useTestimonialStatusBatch.js";
import styles from "../PipelineBrowser.module.css";

const BATCH_SIZE_OPTIONS = [250, 500, 1000] as const;
const DEFAULT_BATCH_SIZE = 500;

type ParsedFile = { name: string; parsed: ParsedTestimonialStatusCsv };

export function TestimonialStatusCsvSection() {
  const [batchSize, setBatchSize] = useState<number>(DEFAULT_BATCH_SIZE);
  const [parsing, setParsing] = useState(false);
  const [file, setFile] = useState<ParsedFile | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const { running, stopped, dryRun, progress, totals, error, operationAt, start, stop, reset } =
    useTestimonialStatusBatch();

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0];
      event.target.value = "";
      if (!selected) return;
      reset();
      setParsing(true);
      setParseError(null);
      setFile(null);
      try {
        setFile({ name: selected.name, parsed: await parseTestimonialStatusCsv(selected) });
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "Failed to parse CSV");
      } finally {
        setParsing(false);
      }
    },
    [reset],
  );

  const rows = file?.parsed.rows ?? [];
  const invalidCount = file?.parsed.invalid.length ?? 0;
  const skipNote = invalidCount > 0 ? `${invalidCount} invalid rows will be skipped` : null;
  const canRun =
    !running && !parsing && file !== null && rows.length > 0 && file.parsed.duplicates.length === 0;

  const handleDryRun = useCallback(() => {
    void start(rows, { batchSize, dryRun: true });
  }, [start, rows, batchSize]);

  const handleApply = useCallback(() => {
    const confirmed = window.confirm(
      `Apply ${rows.length} rows${skipNote ? ` (${skipNote})` : ""}?\n\nThis writes testimonials, processing items and approval requests directly — there is no undo.\n\nContinue?`,
    );
    if (!confirmed) return;
    void start(rows, { batchSize, dryRun: false });
  }, [start, rows, batchSize, skipNote]);

  const hasResult = !running && progress.totalRows > 0;

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Testimonial status from CSV</div>
      <div className={styles.infoBanner}>
        Expected columns: <code>testimonial_id,status</code>. Status must be <code>APPROVED</code>,{" "}
        <code>REJECTED</code> or <code>SUSPENDED</code>. Rows already in the target status are
        skipped. <code>SUSPENDED</code> is applied only to testimonials that are currently APPROVED;
        other rows are reported as not suspendable. Copies and translations change only the
        testimonial row. Rows whose approval request is WITHDRAWN are skipped entirely.
      </div>

      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>Batch size:</span>
        <select
          className={styles.filterSelect}
          aria-label="Batch size"
          value={batchSize}
          disabled={running}
          onChange={e => setBatchSize(Number(e.target.value))}
        >
          {BATCH_SIZE_OPTIONS.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className={styles.syncStateLabel}>CSV file:</span>
        <input
          type="file"
          accept=".csv"
          aria-label="Testimonial status CSV file"
          disabled={running || parsing}
          onChange={handleFileChange}
        />
      </div>

      {parsing && <div className={styles.syncStateMessage}>Parsing CSV…</div>}
      {parseError && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {parseError}
        </div>
      )}
      {file && <ParsedCsvSummary parsed={file.parsed} fileName={file.name} />}

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!canRun}
          onClick={handleDryRun}
        >
          Dry run
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={!canRun}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={!running}
          onClick={stop}
        >
          Stop
        </Button>
        {skipNote && <span className={styles.mappingHint}>{skipNote}</span>}
      </div>

      {running && (
        <>
          <div className={styles.warningBox}>
            Do not close or refresh this tab and do not navigate away from this page. The update
            runs from the browser in batches; leaving stops it mid-way (re-uploading the same file
            is safe, finished rows are counted as unchanged).
          </div>
          <div className={styles.syncStateMessage} role="status">
            {dryRun ? "Dry run" : "Applying"}: batch {progress.batch} / {progress.totalBatches} —
            rows {progress.processedRows} / {progress.totalRows}
          </div>
          <div className={styles.syncStateMessage}>
            Updated {totals.updatedTestimonials} · unchanged {totals.unchanged} · not found{" "}
            {totals.notFound} · withdrawn skipped {totals.withdrawnSkipped} · not suspendable{" "}
            {totals.notSuspendable}
          </div>
        </>
      )}

      {error && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {error}
        </div>
      )}

      {hasResult && (
        <TestimonialStatusRunReport
          totals={totals}
          progress={progress}
          dryRun={dryRun}
          stopped={stopped}
          operationAt={operationAt}
        />
      )}
    </div>
  );
}
